import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MapPin, Truck, Clock, CheckCircle, Package, Navigation, Phone, Calendar, AlertCircle } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const StatusCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  overflow: hidden;
`;

const StatusHeader = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
  padding: 25px;
  text-align: center;
`;

const TrackingNumber = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 10px 0;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  font-weight: 600;
  font-size: 16px;
`;

const MapSection = styled.div`
  padding: 25px;
  border-bottom: 1px solid #e5e7eb;
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 300px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #3b82f6;
  color: #1e40af;
  text-align: center;
  margin-bottom: 20px;
`;

const LocationInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const LocationCard = styled.div`
  background: #f8fafc;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
`;

const LocationTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LocationDetails = styled.div`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const RealTimeUpdates = styled.div`
  padding: 25px;
`;

const UpdatesTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UpdateItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const UpdateIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.type === 'current' ? '#3b82f6' : '#e5e7eb'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.type === 'current' ? 'white' : '#9ca3af'};
  flex-shrink: 0;
`;

const UpdateContent = styled.div`
  flex: 1;
`;

const UpdateTime = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 5px;
`;

const UpdateText = styled.div`
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
  margin-bottom: 3px;
`;

const UpdateLocation = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const DriverInfo = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const DriverTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #d97706;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DriverDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  font-size: 14px;
  color: #92400e;
`;

const EstimatedDelivery = styled.div`
  background: #dcfce7;
  border: 1px solid #22c55e;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  margin-bottom: 20px;
`;

const EstimatedTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #15803d;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const EstimatedTime = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #15803d;
  margin-bottom: 5px;
`;

const EstimatedNote = styled.div`
  font-size: 12px;
  color: #166534;
`;

function DeliveryStatus() {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // 더미 실시간 배송 데이터
  const trackingData = {
    trackingNumber: 'TK123456789',
    status: 'in-transit',
    statusText: '배송중',
    currentLocation: {
      address: '서울시 강남구 테헤란로 152',
      coords: { lat: 37.5036, lng: 127.0439 },
      lastUpdate: '2분 전'
    },
    destination: {
      address: '서울시 강남구 테헤란로 123, 10층',
      coords: { lat: 37.5045, lng: 127.0425 }
    },
    estimatedDelivery: '오늘 오후 3:30',
    driver: {
      name: '김기사',
      phone: '010-1234-5678',
      vehicle: '현대 포터 (서울12가3456)'
    },
    updates: [
      {
        time: '14:25',
        text: '고객님 근처에서 배송 중',
        location: '서울시 강남구',
        type: 'current'
      },
      {
        time: '13:45',
        text: '강남구 배송센터에서 출발',
        location: '강남배송센터',
        type: 'completed'
      },
      {
        time: '09:30',
        text: '서울 허브터미널 도착',
        location: '서울허브',
        type: 'completed'
      },
      {
        time: '어제 22:15',
        text: '상품 발송',
        location: '경기 광명시',
        type: 'completed'
      }
    ]
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'in-transit': return <Truck size={20} />;
      case 'delivered': return <CheckCircle size={20} />;
      default: return <Package size={20} />;
    }
  };

  return (
    <ContentLayout 
      title="실시간 배송 추적"
      subtitle="현재 위치와 실시간 배송 현황을 확인하실 수 있습니다"
    >
      <Container>
        <StatusCard>
          <StatusHeader>
            <TrackingNumber>{trackingData.trackingNumber}</TrackingNumber>
            <StatusBadge>
              {getStatusIcon(trackingData.status)}
              {trackingData.statusText}
            </StatusBadge>
          </StatusHeader>

          <MapSection>
            <MapPlaceholder>
              <MapPin size={48} style={{marginBottom: '15px'}} />
              <div style={{fontSize: '18px', fontWeight: '600', marginBottom: '5px'}}>
                실시간 위치 추적
              </div>
              <div style={{fontSize: '14px', opacity: 0.7}}>
                여기에 실제 지도가 표시됩니다
              </div>
              <div style={{fontSize: '12px', marginTop: '10px'}}>
                Google Maps API 연동 필요
              </div>
            </MapPlaceholder>

            <LocationInfo>
              <LocationCard>
                <LocationTitle>
                  <Navigation size={16} />
                  현재 위치
                </LocationTitle>
                <LocationDetails>
                  <div><strong>{trackingData.currentLocation.address}</strong></div>
                  <div>마지막 업데이트: {trackingData.currentLocation.lastUpdate}</div>
                </LocationDetails>
              </LocationCard>

              <LocationCard>
                <LocationTitle>
                  <MapPin size={16} />
                  배송 목적지
                </LocationTitle>
                <LocationDetails>
                  <div><strong>{trackingData.destination.address}</strong></div>
                  <div>예상 도착시간: {trackingData.estimatedDelivery}</div>
                </LocationDetails>
              </LocationCard>
            </LocationInfo>
          </MapSection>

          <div style={{padding: '25px'}}>
            <EstimatedDelivery>
              <EstimatedTitle>
                <Clock size={20} />
                예상 배송 완료 시간
              </EstimatedTitle>
              <EstimatedTime>{trackingData.estimatedDelivery}</EstimatedTime>
              <EstimatedNote>실시간 교통상황에 따라 변동될 수 있습니다</EstimatedNote>
            </EstimatedDelivery>

            <DriverInfo>
              <DriverTitle>
                <Truck size={16} />
                배송기사 정보
              </DriverTitle>
              <DriverDetails>
                <div>
                  <div><strong>기사명:</strong> {trackingData.driver.name}</div>
                </div>
                <div>
                  <div><strong>연락처:</strong> {trackingData.driver.phone}</div>
                </div>
                <div>
                  <div><strong>차량정보:</strong> {trackingData.driver.vehicle}</div>
                </div>
              </DriverDetails>
            </DriverInfo>
          </div>

          <RealTimeUpdates>
            <UpdatesTitle>
              <AlertCircle size={20} />
              실시간 업데이트
            </UpdatesTitle>
            
            {trackingData.updates.map((update, index) => (
              <UpdateItem key={index}>
                <UpdateIcon type={update.type}>
                  {update.type === 'current' ? <Truck size={16} /> : <CheckCircle size={16} />}
                </UpdateIcon>
                <UpdateContent>
                  <UpdateTime>{update.time}</UpdateTime>
                  <UpdateText>{update.text}</UpdateText>
                  <UpdateLocation>{update.location}</UpdateLocation>
                </UpdateContent>
              </UpdateItem>
            ))}
          </RealTimeUpdates>
        </StatusCard>
      </Container>
    </ContentLayout>
  );
}

export default DeliveryStatus;